import { ProviderType } from 'features/connections'
import { MapDataset, MapType } from 'features/maps/types'
import { getFormattedSource } from './FilterByColumn/queries'

const DATE_REGEX = /^\d{4}\.(0[1-9]|1[012])\.(0[1-9]|[12]\d|3[01])$/
const SEMVER_REGEX =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+)?$/

export const wrapQueryWith = (query: string, alias = '__q1', includeWith = true) => {
  const response = `${includeWith ? 'WITH ' : ''}${alias} AS (
\t${query}
)`
  return response
}

export const isAnalysis = (query) => {
  return query?.includes('WITH __q1')
}

export const getQFromQuery = (query) => {
  const reg = new RegExp(/__q([0-9]?[0-9]?[0-9]) AS \(/, 'gm')
  const matches = query.matchAll(reg)
  if (matches) {
    let lastIndex = 1
    for (const match of matches) {
      lastIndex = parseInt(match[1])
    }
    return lastIndex
  }
  return 1
}

export const removeLastQuerySelect = (query) => {
  const lastQuery = getQFromQuery(query)
  let cleanQuery = query.replace(`SELECT * FROM __q${lastQuery}`, '')
  if (cleanQuery.endsWith('\n')) {
    cleanQuery = cleanQuery.slice(0, -1)
  }
  return cleanQuery
}

export const increaseQueriesNumber = (query, sumBy = 1) => {
  const withReg = new RegExp(/__q([0-9]?[0-9]?[0-9]) AS \(/, 'gm')
  let updatedQuery = query.replaceAll(withReg, (match, qNumber) => `__q${parseInt(qNumber) + sumBy} AS (`)
  const fromAsReg = new RegExp(/__q([0-9]?[0-9]?[0-9]) as /, 'gm') // Match Intersect & ColumnsFromSecondSource FROM
  updatedQuery = updatedQuery.replaceAll(fromAsReg, (match, qNumber) => `__q${parseInt(qNumber) + sumBy} as `)
  const fromQReg = new RegExp(/FROM\s +\t?__q([0-9]?[0-9]?[0-9])$/, 'gm') // Match Buffer FROM
  updatedQuery = updatedQuery.replaceAll(fromQReg, (match, qNumber) =>
    match.replace(`__q${qNumber}`, `__q${parseInt(qNumber) + sumBy}`)
  )
  const fromQWhereReg = new RegExp(/FROM +\t?__q([0-9]?[0-9]?[0-9]) WHERE/, 'gm') // Match FilterByColumn FROM
  updatedQuery = updatedQuery.replaceAll(fromQWhereReg, (match, qNumber) =>
    match.replace(`__q${qNumber}`, `__q${parseInt(qNumber) + sumBy}`)
  )
  return updatedQuery || query
}

export const buildAnalysisQueryFromSource = ({
  source,
  wrapWith = true,
  lastQNumber
}: {
  source: MapDataset
  wrapWith?: boolean
  lastQNumber?: number
}): string => {
  const querySource = source?.queryTemplate || source?.source
  const isBaseAnalysis = source?.type === MapType.query && isAnalysis(querySource)
  const nextQueryNumber = lastQNumber ? lastQNumber + 1 : 1
  const query = isBaseAnalysis
    ? removeLastQuerySelect(querySource)
    : source.type === MapType.query
    ? wrapQueryWith(querySource, `__q${nextQueryNumber}`, wrapWith)
    : wrapQueryWith(`SELECT * FROM ${getFormattedSource({ sourceDataset: source })}`, `__q${nextQueryNumber}`, wrapWith)
  return query
}

export function formatSource(source) {
  return source
    .split('.')
    .map((fragment) => `"${fragment}"`)
    .join('.')
}

function quotePartPGRS(part) {
  if (part && part.match(/^((?!").)*([A-Z])*?(-|\s)/g)) {
    part = `"${part}"`
  }
  return part
}

export function formatPGRSSource(source) {
  return source
    .split('.')
    .map((fragment) => quotePartPGRS(fragment))
    .join('.')
}

export const supportedProviders = [
  ProviderType.REDSHIFT,
  ProviderType.BIGQUERY,
  ProviderType.SNOWFLAKE,
  ProviderType.POSTGRES
]
export const supportedSourceType = [MapType.table, MapType.query]

export function analysisSourceFilter(source) {
  const isSourceSupported = supportedSourceType.includes(source.metadata?.type)
  const isProviderSupported = supportedProviders.includes(source.metadata?.provider || source.providerId)
  const knownSchema = source.metadata?.schema?.length > 0
  return isSourceSupported && isProviderSupported && knownSchema
}

export function checkATValidVersion(version) {
  return version ? SEMVER_REGEX.test(version) || DATE_REGEX.test(version) : false
}

export function checkGreaterOrEqualATVersion(minVersion, currentVersion) {
  /*
    Versioning has changed, from:
    - Date format: `yyyy.mm.dd` to
    - Semver format: `major.minor.patch`
    So for example 0.1.0 is a version newer than 2022.12.15
  */
  if (DATE_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      // NOTE: Support for legacy format `yyyy.mm.dd`
      return currentVersion >= minVersion
    } else if (SEMVER_REGEX.test(currentVersion)) {
      return true
    }
  } else if (SEMVER_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      return false
    } else if (SEMVER_REGEX.test(currentVersion)) {
      // NOTE: Current format `major.minor.patch`
      if (currentVersion === minVersion) {
        return true
      }
      const minNumbers = minVersion.split('.')
      const currentNumbers = currentVersion.split('.')
      for (const [minNumber, currentNumber] of minNumbers.map((n, i) => [Number(n), Number(currentNumbers[i])])) {
        if (currentNumber !== minNumber) {
          return currentNumber >= minNumber
        }
      }
    }
  }

  // Fallback: unknown version format
  return false
}

/*
  Cleaning up SQL sources:
  - Remove leading/trailing whitespace characters
  - Remove trailing semicolon
*/
export function sanitizeSQLSource(source) {
  return source.trim().replace(/;$/g, '')
}

/**
 * Formats string to be used in query.
 *
 * Surrounds in quotes (`'`) and properly escapes special characters
 *
 * Docs
 *  - pg/rs: https://www.postgresql.org/docs/current/sql-syntax-lexical.html#SQL-SYNTAX-STRINGS-ESCAPE
 *  - sf: https://docs.snowflake.com/en/sql-reference/data-types-text#label-single-quoted-string-constants
 *
 * escapes single quotes and backslashes
 */
export function formatSqlValueString(value: string) {
  const withEscapedCharacters = value.replaceAll('\\', '\\\\').replaceAll("'", "''")
  return `'${withEscapedCharacters}'`
}

/**
 * Formats string to be used in BiqQuery query using modern syntax.
 *
 * Surrounds in triple quotes (`"""`) and properly escapes embedded """ and backslashes
 *
 * Docs: biquery: https://cloud.google.com/bigquery/docs/reference/legacy-sql#stringfunctions
 */
export function formatBigQuerySqlValueString(value: string) {
  const withEscapedCharacters = value.replaceAll('\\', '\\\\').replaceAll('"', '\\"')
  return `"""${withEscapedCharacters}"""`
}
